import FormInput from '@/components/UI/FormInput';
import FormSelect from '@/components/UI/FormSelect';
import type { ContextFeatureAllowedValue } from '@/types/appRouter';
import type { TradeLogicRegistryFeatureDto } from '../registry';
import SchemaFieldInput from './SchemaFieldInput';
import { buildFeatureValueField, RANGE_OPERATORS, schemaAllowedValueToInputString, VALUELESS_OPERATORS } from './schemaControlUtils';

interface FeatureConditionValueInputProps {
    feature: TradeLogicRegistryFeatureDto;
    operator: string;
    value: string;
    onChange: (value: string) => void;
}

const formatAllowedValueLabel = (value: ContextFeatureAllowedValue, enumLabels: Record<string, string> | null | undefined): string => {
    const stringKey = String(value);
    const friendlyLabel = enumLabels?.[stringKey];

    if (friendlyLabel && friendlyLabel !== stringKey) {
        return `${friendlyLabel} (${stringKey})`;
    }

    return stringKey;
};

const FeatureConditionValueInput = ({ feature, operator, value, onChange }: FeatureConditionValueInputProps) => {
    const label = RANGE_OPERATORS.has(operator) ? 'Value range' : 'Value';

    if (VALUELESS_OPERATORS.has(operator)) {
        return <FormInput disabled readOnly helpText="No value required for this operator." label={label} value="" />;
    }

    if (RANGE_OPERATORS.has(operator)) {
        return (
            <FormInput
                helpText="Two comma-separated values."
                label={label}
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        );
    }

    const field = buildFeatureValueField(feature, operator);
    const enumLabels = feature.optimizer?.enumLabels ?? null;

    if (field.controlType === 'select' && field.allowedValues.length > 0) {
        return (
            <FormSelect
                label={field.label}
                options={[
                    { value: '', label: 'No value' },
                    ...field.allowedValues.map((allowedValue) => ({
                        value: schemaAllowedValueToInputString(allowedValue),
                        label: formatAllowedValueLabel(allowedValue, enumLabels)
                    }))
                ]}
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        );
    }

    return <SchemaFieldInput field={field} value={value} onChange={onChange} />;
};

export default FeatureConditionValueInput;
